import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { CreditCard, Wallet } from 'lucide-react';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { mountAirwallexDropIn } from '@/lib/airwallex';

interface Props {
  mentorId: string;
  mentorName: string;
  mentorAvatar: string;
  mentorCompany: string;
  consultPrice: number;
  consultDuration: string;
  onClose: () => void;
  onSuccess: (payload: { bookingNo: string; orderStatus: number }) => void;
}

type Step = 'info' | 'payment' | 'success';
type PayType = 31 | 100;

const AIRWALLEX_CONTAINER_ID = 'airwallex-mentor-payment-element';

const PaymentRequestModal = ({
  mentorId,
  mentorName,
  mentorAvatar,
  mentorCompany,
  consultPrice,
  consultDuration,
  onClose,
  onSuccess
}: Props) => {
  const [step, setStep] = useState<Step>('info');
  const [form, setForm] = useState({ name: '', email: '', phone: '', preferTime: '', goal: '' });
  const [errors, setErrors] = useState<Partial<typeof form>>({});
  const [requestError, setRequestError] = useState('');
  const [availableAmount, setAvailableAmount] = useState(0);
  const [payType, setPayType] = useState<PayType>(31);
  const [bookingPayload, setBookingPayload] = useState<Awaited<ReturnType<typeof api.createMentorBooking>> | null>(null);
  const [dropInReady, setDropInReady] = useState(false);
  const [paying, setPaying] = useState(false);
  const [balanceLoaded, setBalanceLoaded] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, saveRedirect } = useAuth();
  const dropInRef = useRef<{ destroy?: () => void } | null>(null);

  const canUseBalance = useMemo(() => availableAmount >= consultPrice || consultPrice === 0, [availableAmount, consultPrice]);
  const payOptions = useMemo(() => {
    const options: Array<{ code: PayType; label: string; icon: 'balance' | 'airwallex'; desc: string }> = [];
    if (canUseBalance) {
      options.push({ code: 100, label: '餘額支付', icon: 'balance', desc: '使用帳戶餘額付款' });
    }
    if (consultPrice > 0) {
      options.push({ code: 31, label: 'Airwallex', icon: 'airwallex', desc: '信用卡、Google Pay' });
    }
    return options;
  }, [canUseBalance, consultPrice]);

  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }
    let active = true;
    void api.getCurrentUser()
      .then((user) => {
        if (!active) {
          return;
        }
        const balance = Number(user.availableAmount ?? 0);
        setAvailableAmount(balance);
        setPayType(balance >= consultPrice ? 100 : 31);
      })
      .catch(() => {
        if (active) {
          setAvailableAmount(0);
          setPayType(31);
        }
      })
      .finally(() => {
        if (active) {
          setBalanceLoaded(true);
        }
      });

    return () => {
      active = false;
    };
  }, [consultPrice, isAuthenticated]);

  useEffect(() => {
    return () => {
      dropInRef.current?.destroy?.();
    };
  }, []);

  useEffect(() => {
    if (step !== 'payment' || payType !== 31 || !bookingPayload?.payMessage) {
      return;
    }
    let cancelled = false;
    dropInRef.current?.destroy?.();
    setDropInReady(false);
    setPaying(true);

    void mountAirwallexDropIn({
      containerId: AIRWALLEX_CONTAINER_ID,
      payMessage: bookingPayload.payMessage,
      locale: 'zh-HK',
      onReady: () => {
        if (!cancelled) {
          setDropInReady(true);
          setPaying(false);
        }
      },
      onCancel: () => {
        if (!cancelled) {
          setPaying(false);
        }
      },
      onError: () => {
        if (!cancelled) {
          setPaying(false);
          setRequestError('支付初始化失敗，請稍後再試');
        }
      },
      onSuccess: () => {
        if (!cancelled) {
          void handleAirwallexSuccess();
        }
      }
    }).then((instance) => {
      dropInRef.current = instance;
    }).catch((error: unknown) => {
      if (!cancelled) {
        setPaying(false);
        setRequestError(error instanceof Error ? error.message : '支付初始化失敗');
      }
    });

    return () => {
      cancelled = true;
      dropInRef.current?.destroy?.();
    };
  }, [bookingPayload?.payMessage, payType, step]);

  const validateInfo = () => {
    const nextErrors: Partial<typeof form> = {};
    if (!form.name.trim()) nextErrors.name = '請填寫姓名';
    if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) nextErrors.email = '請填寫有效信箱';
    if (!form.preferTime) nextErrors.preferTime = '請選擇偏好時段';
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const triggerSuccessClose = (payload: { bookingNo: string; orderStatus: number }) => {
    setRequestError('');
    setStep('success');
    onSuccess(payload);
    setShowSuccess(true);
    onClose();
  };

  const confirmMentorBookingPaid = async (bookingNo?: string | number) => {
    if (!bookingNo) {
      return;
    }
    await new Promise((resolve) => window.setTimeout(resolve, 1500));
    const page = await api.getMyBookings({ pageCurrent: 1, pageSize: 20 });
    const current = page.list.find((item) => String(item.bookingNo) === String(bookingNo));
    if (current && Number(current.orderStatus) !== 2) {
      throw new Error('mentor booking not paid yet');
    }
  };

  const handleAirwallexSuccess = async () => {
    if (!bookingPayload?.id) {
      return;
    }
    setPaying(true);
    setRequestError('');
    try {
      await confirmMentorBookingPaid(bookingPayload.bookingNo);
    } catch {
      // Ignore delayed webhook status check failures and still complete.
    }
    triggerSuccessClose({ bookingNo: String(bookingPayload.bookingNo), orderStatus: 2 });
    setPaying(false);
  };

  const findRecentUnpaidBooking = async () => {
    const page = await api.getMyBookings({ pageCurrent: 1, pageSize: 10 });
    const isRecentOrder = (timestamp?: string) => {
      if (!timestamp) {
        return false;
      }
      const normalized = timestamp.includes('T') ? timestamp : timestamp.replace(' ', 'T');
      const createdAt = new Date(normalized);
      if (Number.isNaN(createdAt.getTime())) {
        return false;
      }
      return Date.now() - createdAt.getTime() <= 30 * 60 * 1000;
    };
    return page.list
      .filter((item) => String(item.lecturerId) === String(mentorId))
      .filter((item) => item.orderStatus === 1)
      .filter((item) => isRecentOrder(item.gmtCreate))
      .sort((a, b) => new Date(b.gmtCreate || 0).getTime() - new Date(a.gmtCreate || 0).getTime())[0];
  };

  const initializeBooking = async () => {
    const latestUser = await api.getCurrentUser();
    const balance = Number(latestUser.availableAmount ?? 0);
    setAvailableAmount(balance);
    setBalanceLoaded(true);
    // For paid bookings, always create a pending Airwallex order first.
    // Users can then explicitly switch to balance pay in the payment step.
    const initialPayType: PayType = consultPrice > 0 ? 31 : 100;

    if (consultPrice > 0) {
      const pendingBooking = await findRecentUnpaidBooking();
      if (pendingBooking?.bookingNo) {
        setPayType(31);
        const resp = await api.continueMentorBookingPay({
          bookingNo: pendingBooking.bookingNo,
          payType: 31,
          quitUrl: window.location.href
        });
        setBookingPayload(resp);
        setStep('payment');
        return;
      }
    }

    setPayType(initialPayType);

    const resp = await api.createMentorBooking({
      lecturerId: mentorId,
      contactName: form.name.trim(),
      contactEmail: form.email.trim(),
      contactPhone: form.phone.trim() || undefined,
      preferTime: form.preferTime,
      consultGoal: form.goal.trim() || undefined,
      payType: initialPayType,
      quitUrl: window.location.href
    });
    setBookingPayload(resp);
    if (initialPayType === 100 && resp.orderStatus === 2) {
      triggerSuccessClose({ bookingNo: String(resp.bookingNo), orderStatus: 2 });
      return;
    }
    setStep('payment');
  };

  const handleInfoNext = () => {
    if (!validateInfo()) {
      return;
    }
    if (!isAuthenticated) {
      saveRedirect(location.pathname + location.search + location.hash);
      onClose();
      navigate(`/login?redirect=${encodeURIComponent(location.pathname + location.search + location.hash)}`);
      return;
    }
    setPaying(true);
    setRequestError('');
    void initializeBooking().catch((error: unknown) => {
      setRequestError(error instanceof Error ? error.message : '建立預約訂單失敗');
    }).finally(() => {
      setPaying(false);
    });
  };

  const handlePayTypeChange = (nextPayType: PayType) => {
    if (nextPayType === payType || !bookingPayload?.bookingNo) {
      return;
    }
    if (nextPayType === 100) {
      setPayType(100);
      setRequestError('');
      dropInRef.current?.destroy?.();
      setDropInReady(false);
      return;
    }
    setPaying(true);
    setRequestError('');
    dropInRef.current?.destroy?.();
    void api.continueMentorBookingPay({
      bookingNo: bookingPayload.bookingNo,
      payType: nextPayType,
      quitUrl: window.location.href
    }).then((resp) => {
      setPayType(nextPayType);
      setBookingPayload(resp);
    }).catch((error: unknown) => {
      setRequestError(error instanceof Error ? error.message : '切換支付方式失敗');
    }).finally(() => {
      setPaying(false);
    });
  };

  const handleBalancePay = () => {
    if (!bookingPayload?.bookingNo) {
      return;
    }
    setPaying(true);
    setRequestError('');
    void api.continueMentorBookingPay({
      bookingNo: bookingPayload.bookingNo,
      payType: 100,
      quitUrl: window.location.href
    }).then((resp) => {
      setBookingPayload(resp);
      if (resp.orderStatus === 2) {
        triggerSuccessClose({ bookingNo: String(resp.bookingNo), orderStatus: 2 });
      }
    }).catch((error: unknown) => {
      setRequestError(error instanceof Error ? error.message : '付款失敗');
    }).finally(() => {
      setPaying(false);
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-6">
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={showSuccess ? undefined : onClose} />

      <div className="relative w-full max-w-5xl max-h-[calc(100vh-1.5rem)] overflow-hidden rounded-3xl bg-white shadow-2xl sm:max-h-[calc(100vh-3rem)]">
        {showSuccess ? (
          <div className="absolute inset-0 z-20 flex items-center justify-center bg-white/90">
            <div className="flex flex-col items-center gap-3 rounded-3xl border border-emerald-100 bg-emerald-50 px-10 py-8 text-center shadow-lg">
              <i className="ri-checkbox-circle-line text-4xl text-emerald-500" />
              <p className="text-base font-bold text-emerald-600">付款成功</p>
              <p className="text-xs text-emerald-500/80">正在關閉視窗...</p>
            </div>
          </div>
        ) : null}
        <div className="flex max-h-[calc(100vh-1.5rem)] flex-col lg:flex-row sm:max-h-[calc(100vh-3rem)]">
          <aside className="w-full border-b border-slate-100 bg-slate-50 p-6 lg:w-[300px] lg:border-b-0 lg:border-r">
            <div className="mb-6 flex items-center gap-3">
              <div className="h-11 w-11 overflow-hidden rounded-full ring-2 ring-slate-200">
                <img src={mentorAvatar} alt={mentorName} className="h-full w-full object-cover object-top" />
              </div>
              <div>
                <p className="text-sm font-bold text-slate-900">{mentorName}</p>
                <p className="text-[11px] text-slate-400">{mentorCompany}</p>
              </div>
            </div>

            <div className="space-y-3">
              {payOptions.map((option) => (
                <button
                  key={option.code}
                  onClick={() => handlePayTypeChange(option.code)}
                  disabled={step !== 'payment' || paying}
                  className={`flex w-full items-center gap-3 rounded-2xl border px-4 py-4 text-left transition-all ${payType === option.code ? 'border-[#0A1628] bg-[#0A1628] text-white' : 'border-slate-200 bg-white text-slate-700 hover:border-slate-300'} disabled:cursor-not-allowed disabled:opacity-60`}
                >
                  <span className={`flex h-9 w-9 items-center justify-center rounded-xl ${payType === option.code ? 'bg-white/15' : 'bg-slate-100'}`}>
                    {option.icon === 'balance'
                      ? <Wallet className={`h-5 w-5 ${payType === option.code ? 'text-white' : 'text-slate-500'}`} />
                      : <CreditCard className={`h-5 w-5 ${payType === option.code ? 'text-white' : 'text-slate-500'}`} />}
                  </span>
                  <div>
                    <p className="text-sm font-bold">{option.label}</p>
                    <p className={`text-[11px] ${payType === option.code ? 'text-white/60' : 'text-slate-400'}`}>{option.desc}</p>
                  </div>
                </button>
              ))}
            </div>

            <div className="mt-8 rounded-2xl border border-slate-200 bg-white p-4">
              <p className="text-xs font-semibold text-slate-400">1V1 專屬視訊諮詢</p>
              <p className="mt-2 text-2xl font-black text-slate-900">${consultPrice}</p>
              <p className="mt-1 text-xs text-slate-400">{consultDuration}</p>
              <div className="mt-4 text-sm text-slate-600">
                <p className="font-semibold">可用餘額</p>
                <p className="mt-1 font-bold">{balanceLoaded ? `$${availableAmount}` : '...'}</p>
              </div>
            </div>
          </aside>

          <main className="flex-1 p-6 lg:p-8">
            <div className="mb-6 flex items-start justify-between gap-4">
              <div>
                <p className="text-2xl font-black text-slate-900">{step === 'success' ? '付款完成' : '導師預約'}</p>
                <p className="mt-1 text-sm text-slate-400">
                  {step === 'info' ? '先確認預約資訊，再完成付款' : step === 'payment' ? '完成付款後即可建立本次預約' : '你可以前往我的預約查看最新狀態'}
                </p>
              </div>
              <button onClick={onClose} disabled={showSuccess} className="flex h-9 w-9 items-center justify-center rounded-full text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700 disabled:opacity-40">
                <i className="ri-close-line text-xl" />
              </button>
            </div>

            {step === 'info' ? (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="mb-1.5 block text-[11px] font-semibold text-slate-500">姓名 <span className="text-rose-400">*</span></label>
                    <input
                      type="text"
                      value={form.name}
                      onChange={(event) => setForm({ ...form, name: event.target.value })}
                      className={`w-full rounded-2xl border px-4 py-3 text-sm outline-none ${errors.name ? 'border-rose-300' : 'border-slate-200'}`}
                      placeholder="你的姓名"
                    />
                    {errors.name ? <p className="mt-1 text-[10px] text-rose-500">{errors.name}</p> : null}
                  </div>
                  <div>
                    <label className="mb-1.5 block text-[11px] font-semibold text-slate-500">電話</label>
                    <input
                      type="tel"
                      value={form.phone}
                      onChange={(event) => setForm({ ...form, phone: event.target.value })}
                      className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm outline-none"
                      placeholder="+886 912 345 678"
                    />
                  </div>
                </div>

                <div>
                  <label className="mb-1.5 block text-[11px] font-semibold text-slate-500">信箱 <span className="text-rose-400">*</span></label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={(event) => setForm({ ...form, email: event.target.value })}
                    className={`w-full rounded-2xl border px-4 py-3 text-sm outline-none ${errors.email ? 'border-rose-300' : 'border-slate-200'}`}
                    placeholder="用於接收預約確認"
                  />
                  {errors.email ? <p className="mt-1 text-[10px] text-rose-500">{errors.email}</p> : null}
                </div>

                <div>
                  <label className="mb-1.5 block text-[11px] font-semibold text-slate-500">偏好諮詢時段 <span className="text-rose-400">*</span></label>
                  <select
                    value={form.preferTime}
                    onChange={(event) => setForm({ ...form, preferTime: event.target.value })}
                    className={`w-full rounded-2xl border bg-white px-4 py-3 text-sm outline-none ${errors.preferTime ? 'border-rose-300' : 'border-slate-200'}`}
                  >
                    <option value="">請選擇...</option>
                    <option value="工作日早上（9:00-12:00）">工作日早上（9:00-12:00）</option>
                    <option value="工作日下午（13:00-18:00）">工作日下午（13:00-18:00）</option>
                    <option value="工作日晚上（19:00-22:00）">工作日晚上（19:00-22:00）</option>
                    <option value="週末早上（9:00-12:00）">週末早上（9:00-12:00）</option>
                    <option value="週末下午（13:00-18:00）">週末下午（13:00-18:00）</option>
                  </select>
                  {errors.preferTime ? <p className="mt-1 text-[10px] text-rose-500">{errors.preferTime}</p> : null}
                </div>

                <div>
                  <label className="mb-1.5 block text-[11px] font-semibold text-slate-500">本次諮詢目標</label>
                  <textarea
                    value={form.goal}
                    onChange={(event) => setForm({ ...form, goal: event.target.value.slice(0, 200) })}
                    rows={4}
                    className="w-full resize-none rounded-2xl border border-slate-200 px-4 py-3 text-sm outline-none"
                    placeholder="簡單描述你想聚焦的問題"
                  />
                  <p className="mt-1 text-right text-[10px] text-slate-400">{form.goal.length}/200</p>
                </div>

                {requestError ? <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-600">{requestError}</div> : null}

                <button
                  onClick={handleInfoNext}
                  disabled={paying}
                  className="flex w-full items-center justify-center gap-2 rounded-2xl bg-[#0A1628] py-4 text-sm font-bold text-white transition-colors hover:bg-[#12233f] disabled:opacity-60"
                >
                  {paying ? <><i className="ri-loader-4-line animate-spin" />建立訂單中...</> : <>下一步：前往付款<i className="ri-arrow-right-line" /></>}
                </button>
              </div>
            ) : null}

            {step === 'payment' ? (
              <div className="space-y-5">
                {payType === 31 ? (
                  <div className="rounded-2xl border border-slate-200 bg-white p-5">
                    <div className="mb-4 flex items-center justify-between">
                      <div>
                        <p className="text-sm font-bold text-slate-900">Airwallex 結帳</p>
                        <p className="text-xs text-slate-400">信用卡、Google Pay</p>
                      </div>
                      {!dropInReady ? <span className="text-xs text-slate-400">載入中</span> : null}
                    </div>
                    <div id={AIRWALLEX_CONTAINER_ID} className="min-h-[320px]" />
                  </div>
                ) : (
                  <div className="rounded-2xl border border-emerald-100 bg-emerald-50 p-6">
                    <p className="text-sm font-bold text-emerald-700">餘額支付</p>
                    <p className="mt-2 text-3xl font-black text-slate-900">${availableAmount}</p>
                    <p className="mt-2 text-sm text-slate-500">將從你的帳戶餘額中直接扣除本次諮詢費用。</p>
                    <button
                      onClick={handleBalancePay}
                      disabled={paying}
                      className="mt-6 w-full rounded-2xl bg-emerald-600 py-4 text-sm font-bold text-white transition-colors hover:bg-emerald-700 disabled:opacity-60"
                    >
                      {paying ? '付款處理中...' : '確認餘額付款'}
                    </button>
                  </div>
                )}

                {requestError ? <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-600">{requestError}</div> : null}

                <div className="flex items-center justify-center gap-1.5 text-[11px] text-slate-400">
                  <i className="ri-shield-check-line text-emerald-500 text-xs" />
                  <span>購買後 7 天無理由退款</span>
                  <span className="text-slate-300">·</span>
                  <Link to="/legal?tab=refund" className="font-medium text-violet-500 underline underline-offset-2 transition-colors hover:text-violet-700">
                    查看退款政策
                  </Link>
                </div>
              </div>
            ) : null}

            {step === 'success' ? (
              <div className="py-12 text-center">
                <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-emerald-50">
                  <i className="ri-checkbox-circle-line text-3xl text-emerald-500" />
                </div>
                <h3 className="text-xl font-black text-slate-900">預約訂單已完成</h3>
                <p className="mt-2 text-sm text-slate-500">訂單號：<span className="font-mono font-semibold text-slate-700">{bookingPayload?.bookingNo}</span></p>
                <div className="mt-8 space-y-3">
                  <button
                    onClick={() => {
                      onClose();
                      navigate('/account?tab=bookings');
                    }}
                    className="w-full rounded-2xl bg-[#0A1628] py-4 text-sm font-bold text-white transition-colors hover:bg-[#12233f]"
                  >
                    查看我的預約
                  </button>
                  <button
                    onClick={() => {
                      onClose();
                      navigate('/mentors');
                    }}
                    className="w-full text-sm font-semibold text-violet-600 transition-colors hover:text-violet-700"
                  >
                    瀏覽更多導師
                  </button>
                </div>
              </div>
            ) : null}
          </main>
        </div>
      </div>
    </div>
  );
};

export default PaymentRequestModal;
